/**
 * 216 (4) (DIRECTION §0.12; GameConfig.roads): a match's roads on a planet — the planet's settlements (core/settlements.ts)
 * and the sides' bases, joined by the route graph's roads (core/planetRoutes.ts planetRoads). Null with the switch off, or
 * fewer than two ends. Pure.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { planetRoads, planetRouteGraph, type Roads } from './planetRoutes';
import { planetSettlements } from './settlements';

export function roadsOf(state: GameState, planetIndex: number): Roads | null {
  if (!state.config.roads) return null;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ends: Vec3[] = planetSettlements(state, planetIndex).map(s => s.dir);
  for (const b of state.board?.bases ?? []) if (b.planetIndex === planetIndex) ends.push(b.dir);
  if (!state.board) for (const d of Object.values(state.actionHomes ?? {})) ends.push(d);
  return planetRoads(planet, ends);
}

const linesMemo = new WeakMap<Roads, Vec3[][]>();

/**
 * The roads as lines for the planet table (68.42): `roads`' edges chained into polylines of vertex directions, each run
 * from an end or a fork to the next (a loop from its lowest vertex), in vertex order. Pure, memoised per network.
 */
export function roadLines(planet: PlanetState, roads: Roads): Vec3[][] {
  const hit = linesMemo.get(roads);
  if (hit) return hit;
  const g = planetRouteGraph(planet), n = g.count;
  const adj = new Map<number, number[]>();
  const link = (a: number, b: number) => { const l = adj.get(a); if (l) l.push(b); else adj.set(a, [b]); };
  for (const k of roads.edges) { const a = Math.floor(k / n), b = k - a * n; link(a, b); link(b, a); }
  for (const l of adj.values()) l.sort((a, b) => a - b);
  const used = new Set<number>(), key = (a: number, b: number) => a < b ? a * n + b : b * n + a;
  const dir = (v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });
  const out: Vec3[][] = [];
  const walk = (from: number, to: number) => {
    const line = [dir(from)];
    let prev = from, v = to;
    used.add(key(from, to));
    for (;;) {
      line.push(dir(v));
      const nb = adj.get(v)!;
      if (nb.length !== 2) break;
      const next = nb[0] === prev ? nb[1] : nb[0];
      if (used.has(key(v, next))) break;
      used.add(key(v, next));
      prev = v; v = next;
    }
    out.push(line);
  };
  const verts = [...adj.keys()].sort((a, b) => a - b);
  for (const pass of [0, 1]) for (const v of verts) {   // ends and forks first, then what is left (loops)
    if (pass === 0 && adj.get(v)!.length === 2) continue;
    for (const w of adj.get(v)!) if (!used.has(key(v, w))) walk(v, w);
  }
  linesMemo.set(roads, out);
  return out;
}
